import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import Location from "../../hooks/Location";
import { useFirestoreGeneral, useFirestore, useFirestoreNoCompagnyGeneralArrayContains } from '../../firebase/useFirestore';
import { db, timestamp } from '../../firebase/config';
import spinner from "../../images/spinner-ripple.svg";
import { useState } from "react";
import { v4 as uuid } from 'uuid';
import { client } from '../../hooks/Client';
import firebase from '../../firebase/compat';
import CompagnyMeta from '../../components/portfolio/CompagnyMeta';
import notDeleted from '../../helpers/notDeleted';
import { duplicateMatrixRows, duplicateMatrixColumns, createQuestionFromSynchronisation } from '../../components/synchronisations/synchronisationActions';
import EffectMeta from '../../components/effects/EffectMeta';
import Tooltip from '../../components/common/Tooltip';
import OutputMeta from '../../components/outputs/OutputMeta';
import deleteIcon from '../../images/icons/delete-icon.png';
import editIcon from '../../images/icons/edit-icon.png';
import DeleteModal from "../../components/common/DeleteModal";
import FieldMeta from '../../components/Questionnaire/FieldMeta';
import TargetgroupMeta from '../../components/stakeholders/TargetgroupMeta';
import CustomEventFieldMeta from '../../components/events/CustomEventFieldMeta';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';
import scaleStartLabel from '../../helpers/questionnaire/scaleStartLabel';

const SynchronisationBuilder = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [activityId, setActivityId] = useState('');
  const [outputTitle, setOutputTitle] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState('');
  const [deleteProject, setDeleteProject] = useState('Synchronisatie');
  const [effectId, setEffectId] = useState('');

  // Hooks
  const syncID = Location()[3];

  // Firestore
  const syncs = useFirestoreNoCompagnyGeneralArrayContains('Synchronisations', 'ID', syncID ? syncID : '123', 'Members', client);
  const outputs = useFirestore('Outputs');
  const activities = useFirestore('Activities');
  const effects = useFirestore('OutputEffects');
  const targetgroups = useFirestore('Stakeholders');
  const MSIEffects = useFirestoreGeneral('OutputEffects', 'Standard', 'true');
  const customEventFields = useFirestore('CustomEventFields');

  // Status label + color
  const status = (statusCode) => {
    switch (statusCode) {
      case 'requested': return { text: 'Aangevraagd', color: '#FFA500' };
      case 'accepted':  return { text: 'Geaccepteerd', color: '#008000' };
      case 'paired':    return { text: 'Gekoppeld', color: '#008000' };
      case 'declined':  return { text: 'Geweigerd', color: '#FF0000' };
      case 'deleted':   return { text: 'Verwijderd', color: '#000000' };
      case "deleted-project": return { text: "Verwijderd door project", color: "#000000" };
      default:          return { text: 'Onbekende status', color: '#000000' };
    }
  };

  const syncType = (type) => {
    switch (type) {
      case 'Effect':     return 'Effect';
      case 'MSIEffect':  return 'Meetstandaard effect';
      case 'Output':     return 'Output';
      case 'Question':   return 'Vraag';
      case 'Targetgroup':return 'Doelgroep';
      case 'CustomEvent': return 'Custom veld'; 
      default:           return 'Onbekend';
    }
  };

  // Actions
  const acceptSyncHandler = (e) => {
    const docid = e.currentTarget.dataset.docid;
    db.collection('Synchronisations').doc(docid).update({ Status: 'accepted' });
  };

  const declineSyncHandler = (e) => {
    const docid = e.currentTarget.dataset.docid;
    db.collection('Synchronisations').doc(docid).update({ Status: 'declined' });
  };

  const projectOutputHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const id = e.currentTarget.value;
    if (!id) return;
    await db.collection('Synchronisations').doc(docid).update({
      ProjectOutput: id,
      Status: 'paired'
    });
  };

  const createNewOutput = async (e) => {
    const id = e.currentTarget.dataset.id;
    const docid = e.currentTarget.dataset.docid;
    if (!id || !docid || !activityId) return;

    const output = await db.collection('Outputs').where('ID', '==', id).get();
    for (const doc of output.docs) {
      const newOutputId = uuid();
      await db.collection('Outputs').add({
        ID: newOutputId,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Title: doc.data().Title,
        Position: (outputs?.length || 0) + 1,
        ActivityID: activityId,
        type: 'manual',
        LinkParticipantsToOutputs: doc.data().LinkParticipantsToOutputs || 'true'
      });
      await db.collection('Synchronisations').doc(docid).update({
        ProjectOutput: newOutputId,
        Status: 'paired'
      });
    }
  };

  // Create MSIEffect and indicator into project
  const projectEffectHandler = async (e) => {
    setLoading(true);
    try {
      const docid = e.currentTarget.dataset.docid;
      const effectId = e.currentTarget.dataset.effectid;
      if (!docid || !effectId) return;

      const [effectsSnap, effectSnap] = await Promise.all([
        db.collection('OutputEffects').where('CompagnyID', '==', client).get(),
        db.collection('OutputEffects').where('ID', '==', effectId).limit(1).get(),
      ]);
      // Don't copy a soft-deleted source effect (ADR-008).
      const effectData = effectSnap.docs.map((d) => d.data()).filter(notDeleted)[0];
      if (!effectData) return;

      const newEffectId = uuid();

      // Create the project effect
      await db.collection('OutputEffects').add({
        Effect: effectData.Effect,
        Position: effectsSnap.docs.length + 1,
        ID: newEffectId,
        Compagny: client,
        Term: 'long',
        CompagnyID: client,
        Timestamp: timestamp,
        type: 'manual',
        MSIId: effectData.MSIId || '',
        Standard: effectData.Standard === 'true' ? 'true' : 'false',
      });

      // Find ONE matrix indicator in portfolio to copy
      const indicatorsSnap = await db.collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', effectId)
        .get();

      const matrixInds = indicatorsSnap.docs
        .map(d => d.data())
        .filter(ind => ind.Type === 'matrix-one' || ind.Type === 'matrix-multiple')
        .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));

      let createdFieldId = null;

      if (matrixInds.length) {
        const ind = matrixInds[0];
        createdFieldId = uuid();

        await db.collection('QuestionnaireFields').doc(createdFieldId).set({
          Compagny: client,
          CompagnyID: client,
          ID: createdFieldId,
          Timestamp: timestamp,
          QuestionnaireID: '',
          Type: ind.Type,
          Question: ind.Question || '',
          Explainer: ind.Explainer || '',
          ReachStart: ind.ReachStart ?? 1,
          // Beide spellingen (#313).
          ReachStartLable: scaleStartLabel(ind),
          ReachStartLabel: scaleStartLabel(ind),
          ReachEnd: ind.ReachEnd ?? 5,
          ReachEndLabel: ind.ReachEndLabel || '',
          Multiple: [],
          SectionID: '',
          EffectId: firebase.firestore.FieldValue.arrayUnion(newEffectId),
          Indicator: 'true',
          LiveReportHidden: false,
          Standard: ind.Standard === 'true' ? 'true' : 'false',
          MatrixBipolar: ind.MatrixBipolar === true,
          MSIId: ind.MSIId || '',
          SyncId: syncID || '',
          Position: ind.Position || 1,
          IndicatorPosition: 1,
        });

        // Copy only matrix parts
        await Promise.all([
          duplicateMatrixRows(ind.ID, createdFieldId, ind.Question),
          duplicateMatrixColumns(ind.ID, createdFieldId, ind.Question),
        ]);
      }

      // Update synchronisation with project effect + question (if created)
      const updatePayload = {
        ProjectEffect: newEffectId,
        Status: 'paired',
      };
      if (createdFieldId) updatePayload.ProjectQuestion = createdFieldId;

      await db.collection('Synchronisations').doc(docid).update(updatePayload);
    } finally {
      setLoading(false);
    }
  };

  // Pair MSI effect to existing project effect
  const pairToExcistingMSIEffect = async (e) => {
    const projectEffectId = e.target.value;
    const syncid = e.target.dataset.syncid;
    const docid = e.target.dataset.docid;
    if (!projectEffectId || !docid) return;

    setLoading(true);
    try {
      // 1) Fetch all indicators linked to this effect
      const indicatorsSnap = await db
        .collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', projectEffectId)
        .where('Standard', '==', 'true')
        .get();

      // 2) Map and filter for standard indicators
      const indicators = indicatorsSnap.docs.map(d => ({ ref: d.ref, ...d.data() }));

      console.log('Standard indicators found:', indicators);

      // 3) Pick the first standard indicator as primary (if any)
      const primary = indicators[0];
      const primaryQuestionId = primary?.ID || null;

      // 4) Create batch for atomic writes
      const batch = db.batch();
      const syncRef = db.collection('Synchronisations').doc(docid);

      const syncPayload = {
        ProjectEffect: projectEffectId,
        Status: 'paired',
      };
      if (primaryQuestionId) syncPayload.ProjectQuestion = primaryQuestionId;

      batch.update(syncRef, syncPayload);

      // 5) Add SyncId to all related indicators
      indicators.forEach(ind => {
        batch.update(ind.ref, { SyncId: syncid });
      });

      // 6) Commit everything
      await batch.commit();
    } finally {
      setLoading(false);
    }
  };


  // Zelfde handeling als in synchronisationActions (die is getest). Hier stond
  // een kopie die de eindtekst als 'ReachEndLable' wegschreef en de begintekst
  // alleen als 'ReachStartLabel' — beide vielen daardoor weg (#313).
  const createNewQuestion = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const portfolioQuestionId = e.currentTarget.dataset.id;
    if (!docid || !portfolioQuestionId || !effectId) return;

    await createQuestionFromSynchronisation({ docid, Question: portfolioQuestionId }, effectId);
  };

  const editPairedSync = (e) => {
    const docid = e.currentTarget.dataset.docid;
    db.collection('Synchronisations').doc(docid).update({
      Status: 'accepted',
      ProjectOutput: null,
      ProjectEffect: null
    });
  };

  const deleteSyncHandler = (e) => {
    const docid = e.currentTarget.dataset.docid;
    const type = e.currentTarget.dataset.synctype;

    // Het venster zei altijd hetzelfde ('Synchronisatie'), ongeacht welke
    // koppeling je aanklikte. Bij meerdere koppelingen was niet te zien welke
    // je op het punt stond te verwijderen.
    setDeleteProject(type ? `de koppeling van type ${type}` : 'deze koppeling');
    setDeleteModal(true);
    setDeleteDocid(docid);
  };

  const updateSync = async () => {
    await db.collection('Synchronisations').doc(deleteDocid).update({ Status: 'deleted' });
    setDeleteDocid('');
    setDeleteModal(false);
  };

  // Project targetgroup handler
  const projectTargetgroupHandler = async (e) => {
    setLoading(true);

    const projectTargetgroupId = e.target.options[e.target.selectedIndex].value;
    const docid = e.target.dataset.docid;

    await db.collection('Synchronisations').doc(docid).update({
      ProjectTargetgroup: projectTargetgroupId
    });

    setLoading(false);
  };

  // Create new targetgroup
  const createNewTargetgroup = async (e) => {
    setLoading(true);

    const syncId = e.currentTarget.dataset.syncid;
    const docid = e.currentTarget.dataset.docid;
    const portfolioTargetgroupId = e.currentTarget.dataset.portfoliotargetgroup;

    const portfolioTargetgroupMeta = await db.collection('Stakeholders')
      .where('ID', '==', portfolioTargetgroupId)
      .limit(1)
      .get();

    const tgDoc = portfolioTargetgroupMeta.docs[0]?.data();
    const name = tgDoc ? tgDoc.Organisation : 'Nieuwe doelgroep';

    // Create new targetgroup in project
    const newTargetgroupId = uuid();
    await db.collection('Stakeholders').doc(newTargetgroupId).set({
      ID: newTargetgroupId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Organisation: name,
      Type: 'targetgroup',
      Position: (targetgroups?.length || 0) + 1,
      type: 'manual'
    });

    // Update all syncs with this new targetgroup
    const syncsSnap = await db.collection('Synchronisations')
      .where('Type', '==', 'Targetgroup')
      .where('Status', '==', 'accepted')
      .get();

    const batch = db.batch();
    syncsSnap.docs.forEach(doc => {
      const syncRef = db.collection('Synchronisations').doc(doc.id);
      batch.update(syncRef, {
        ProjectTargetgroup: newTargetgroupId,
        Status: 'paired'
      });
    });
    await batch.commit();

    setLoading(false);
  };

  // Create new custom event field
  const createNewCustomEventField = async (e) => {
    setLoading(true);
    const syncId = e.currentTarget.dataset.syncid;
    const docid = e.currentTarget.dataset.docid;
    const portfolioFieldId = e.currentTarget.dataset.portfoliocustomeventfield;

    const portfolioFieldMeta = await db.collection('CustomEventFields')
      .where('ID', '==', portfolioFieldId)
      .limit(1)
      .get();
    const fieldDoc = portfolioFieldMeta.docs[0]?.data();
    const fieldTitle = fieldDoc ? fieldDoc.Title : 'Nieuw custom veld';
    const fieldType = fieldDoc ? fieldDoc.Type : 'open';
    const newFieldId = uuid();

    await db.collection('CustomEventFields').doc(newFieldId).set({
      ID: newFieldId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: fieldTitle,
      Type: fieldType,
      Position: (customEventFields?.length || 0) + 1,
      type: 'manual'
    });

    if(fieldType === 'multiple-multiple' || fieldType === 'multiple-one') {
      const options = await db.collection('MultipleQuestionOptions').where('Field', '==', portfolioFieldId).get();

      await Promise.all(options.docs.map(d =>
        db.collection('MultipleQuestionOptions').doc().set({
          Option: d.data().Option,
          Field: newFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: d.data().Position,
          ID: uuid(),
        })
      ));

    };

    await db.collection('Synchronisations').doc(docid).update({
      ProjectCustomEventField: newFieldId,
      Status: 'paired'
    });

    setLoading(false);
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <SyncOutlinedIcon />
          <h1>Synchronisatie</h1>
        </div>

        <ReadOnlyGuard edit="reports.edit">
          {(canEdit) => (
          <div className='dashboard-container'>
          {syncs && syncs.map((sync) => (
            <div key={sync.ID} id='sync-detail-container'>
              {/* Portfolio organisation */}
              <div className='sync-item-container'>
                <div className='activity-meta-title-container'>
                  <p><b>Portfolio-organisatie</b></p>
                </div>
                <p className='sync-content'><CompagnyMeta id={sync.Parent} /></p>
              </div>

              {/* Type */}
              <div className='sync-item-container'>
                <div className='activity-meta-title-container'>
                  <p><b>Type</b></p>
                </div>
                <p className='sync-content'>{syncType(sync.Type)}</p>
              </div>

              {/* Output name */}
              {sync.Type === 'Output' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio output</b></p>
                  </div>
                  <p className='sync-content'><OutputMeta id={sync.Output} /></p>
                </div>
              )}

              {/* Effect name */}
              {sync.Type === 'Effect' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio effect</b></p>
                  </div>
                  <p className='sync-content'><EffectMeta item={sync.Effect} /></p>
                </div>
              )}

              {/* MSIEffect name (show as effect label) */}
              {sync.Type === 'MSIEffect' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio meetstandaard effect</b></p>
                  </div>
                  <p className='sync-content'><EffectMeta item={sync.Effect} /></p>
                </div>
              )}

              {/* Question name */}
              {sync.Type === 'Question' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio vraag</b></p>
                  </div>
                  <p className='sync-content'><FieldMeta item={sync.Question} /></p>
                </div>
              )}

              {/* Targetgroup name */}
              {sync.Type === 'Targetgroup' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio doelgroep</b></p>
                  </div>
                  <p className='sync-content'><TargetgroupMeta targetgroupID={sync.Targetgroup} /></p>
                </div>
              )}

              {/* Custom field name (CustomEvent) */}
              {sync.Type === 'CustomEvent' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Portfolio custom veld</b></p>
                  </div>
                  <p className='sync-content'>
                    <CustomEventFieldMeta fieldId={sync.CustomEvent} />   {/* 👈 HIER */}
                  </p>
                </div>
              )}

              {/* Status */}
              <div className='sync-item-container'>
                <div className='activity-meta-title-container'>
                  <p><b>Status</b></p>
                </div>
                <div className='sync-status-container'>
                  <p className='sync-content' style={{ color: status(sync.Status).color }}>
                    {status(sync.Status).text}
                  </p>
                  {canEdit && (sync.Status === 'accepted' || sync.Status === 'paired') && (
                    <Tooltip content='Koppeling verwijderen' top='-60px' width='20px'>
                      <img
                        src={deleteIcon}
                        alt='delete'
                        className='delete-icon'
                        data-docid={sync.docid}
                        data-synctype={sync.Type}
                        onClick={deleteSyncHandler}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Accept / Decline */}
              <div className='sync-item-container'>
                <div className='activity-meta-title-container'>
                  <p><b>Accepteren of weigeren</b></p>
                </div>

                {sync.Status === 'requested' && (
                  <>
                    {loading ? (
                      <img src={spinner} alt='loading' className='spinner' />
                    ) : (
                      <div className='sync-content'>
                        <button
                          className='button-simple'
                          data-docid={sync.docid}
                          data-syncid={sync.ID}
                          onClick={acceptSyncHandler}
                        >
                          Accepteren
                        </button>
                        <button
                          className='button-simple'
                          id='decline-sync-button'
                          data-docid={sync.docid}
                          data-syncid={sync.ID}
                          onClick={declineSyncHandler}
                        >
                          Weigeren
                        </button>
                      </div>
                    )}
                  </>
                )}

                {sync.Status === 'accepted' && <p className='sync-content'>De synchronisatie is geaccepteerd</p>}
                {sync.Status === 'declined' && <p className='sync-content'>De synchronisatie is geweigerd</p>}
                {sync.Status === 'deleted' && <p className='sync-content'>De synchronisatie is verwijderd</p>}
                {sync.Status === 'paired'   && <p className='sync-content'>De synchronisatie is gekoppeld</p>}
              </div>

              {/* Accepted → Output */}
              {sync.Status === 'accepted' && sync.Type === 'Output' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppeld output</b></p>
                  </div>
                  <div>
                    {sync.ProjectOutput ? (
                      <p className='sync-content'>{outputTitle}</p>
                    ) : (
                      <div>
                        <p><b>Selecteer een bestaande output</b></p>
                        <select data-syncid={sync.ID} data-docid={sync.docid} onChange={projectOutputHandler}>
                          <option value=''>-- Selecteer een output --</option>
                          {outputs && outputs.map((output) => (
                            <option key={output.ID} value={output.ID}>{output.Title}</option>
                          ))}
                        </select>
                        <p><b>- of creëer een nieuwe output</b></p>
                        <select onChange={(e) => setActivityId(e.target.value)}>
                          <option value=''>-- Selecteer een activiteit --</option>
                          {activities && activities.map((activity) => (
                            <option key={activity.ID} value={activity.ID}>{activity.Activity}</option>
                          ))}
                        </select>
                        <div
                          style={{ display: activityId ? 'block' : 'none' }}
                          className='button-container'
                          id='sync-builder-create-output-button-container'
                        >
                          <button data-id={sync.Output} data-docid={sync.docid} onClick={createNewOutput}>
                            Creëer output
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Accepted → MSIEffect */}
              {sync.Status === 'accepted' && sync.Type === 'MSIEffect' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppeld effect</b></p>
                  </div>
                    <div>
                      <select data-syncid={sync.ID} data-docid={sync.docid} onChange={pairToExcistingMSIEffect}>
                        <option value="">-- Selecteer een Meetstandaard effect --</option>
                        {MSIEffects && MSIEffects.map((effect) => (
                          <option key={effect.ID} value={effect.ID}>{effect.Effect}</option>
                        ))}
                      </select>
                      <p>- of -</p>
                      <div>
                        <button
                          data-syncitemid={sync.SyncItem}
                          data-docid={sync.docid}
                          data-effectid={sync.Effect}
                          data-type={sync.Type}
                          onClick={projectEffectHandler}
                        >
                          Effect toevoegen aan Theory of Change
                        </button>
                      </div>
                    </div>
                </div>
              )}

              {/* Accepted → Question */}
              {sync.Status === 'accepted' && sync.Type === 'Question' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppelde vraag</b></p>
                  </div>
                  <div>
                    {sync.ProjectQuestion ? (
                      <p className='sync-content'>{outputTitle}</p>
                    ) : (
                      <div>
                        <p><b>Creëer een nieuwe vraag</b></p>
                        <select onChange={(e) => setEffectId(e.target.value)}>
                          <option value=''>-- Selecteer een effect --</option>
                          {effects && effects.map((effect) => (
                            <option key={effect.ID} value={effect.ID}>{effect.Effect}</option>
                          ))}
                        </select>
                        <div
                          style={{ display: effectId ? 'block' : 'none' }}
                          className='button-container'
                          id='sync-builder-create-output-button-container'
                        >
                          <button data-id={sync.Question} data-docid={sync.docid} onClick={createNewQuestion}>
                            Creëer vraag
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Accepted → Targetgroup */}
              {sync.Status === 'accepted' && sync.Type === 'Targetgroup' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppelde doelgroep</b></p>
                  </div>
                  <div>
                    <p>Selecteer een bestaande doelgroep</p>
                    <select data-syncid={sync.ID} data-docid={sync.docid} onChange={projectTargetgroupHandler}>
                      <option value=''>-- Selecteer een doelgroep --</option>
                      {targetgroups && targetgroups.map((group) => (
                        <option key={group.ID} value={group.ID}>
                          <TargetgroupMeta targetgroupID={group.ID} />
                        </option>
                      ))}
                    </select>
                    <p>-- of creëer een nieuwe doelgroep --</p>
                    <button data-syncid={sync.ID} data-portfoliotargetgroup={sync.Targetgroup} data-docid={sync.docid} onClick={createNewTargetgroup}>Creëer doelgroep</button>
                  </div>
                </div>
              )}

              {/* Accepted → Custom event */}
              {sync.Status === 'accepted' && sync.Type === 'CustomEvent' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppeld custom veld</b></p>
                  </div>
                  <div>
                    <p>Selecteer een bestaand custom veld</p>
                    <select data-syncid={sync.ID} data-docid={sync.docid} onChange={projectTargetgroupHandler}>
                      <option value=''>-- Selecteer een custom veld --</option>
                      {customEventFields && customEventFields.map((field) => (
                        <option key={field.ID} value={field.ID}>
                          <CustomEventFieldMeta fieldId={field.ID} />
                        </option>
                      ))}
                    </select>
                    <p>-- of creëer een nieuw custom veld --</p>
                    <button data-syncid={sync.ID} data-portfoliocustomeventfield={sync.CustomEvent} data-docid={sync.docid} onClick={createNewCustomEventField}>Creëer custom veld</button>
                  </div>
                </div>
              )}

              {/* Paired → Effect */}
              {sync.Status === 'paired' && sync.Type === 'Effect' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppeld effect</b></p>
                  </div>
                  <div id='sync-builder-paired-effect-title-container'>
                    <p className='sync-content'><EffectMeta item={sync.ProjectEffect} /></p>
                    {canEdit && (
                      <Tooltip content='Bewerk gekoppeld effect' top='-60px'>
                        <img
                          src={editIcon}
                          alt='edit'
                          className='edit-icon'
                          data-docid={sync.docid}
                          onClick={editPairedSync}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}

              {/* Paired → Output */}
              {sync.Status === 'paired' && sync.Type === 'Output' && (
                <div className='sync-item-container'>
                  <div className='activity-meta-title-container'>
                    <p><b>Gekoppeld output</b></p>
                  </div>
                  <div id='sync-builder-paired-effect-title-container'>
                    <p className='sync-content'><OutputMeta id={sync.ProjectOutput} /></p>
                    {canEdit && (
                      <Tooltip content='Bewerk gekoppeld output' top='-60px'>
                        <img
                          src={editIcon}
                          alt='edit'
                          className='edit-icon'
                          data-docid={sync.docid}
                          onClick={editPairedSync}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          </div>
          )}
        </ReadOnlyGuard>
      </div>

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        setDeleteDocid={setDeleteDocid}
        deleteItem={updateSync}
        deleteName={deleteProject}
        softDelete
      />
    </div>
  );
};

export default SynchronisationBuilder;
